import { FormikErrors, FormikValues } from "formik";
import { Schema } from "yup";
import { SelectType } from "@almafintech/react-components/InputSelect";
import { AppDispatch, RootState } from "../app/store";
import { FormikProps } from "formik";
import { Key, ReactNode } from "react";
import { ButtonProps, TabsProps } from "@almafintech/react-components";
import { NavigateFunction } from "react-router-dom";
import { Values } from "@almafintech/react-components/InputSelect/types";

export type OnboardingType =
  | "PHYSICAL"
  | "JURIDICAL"
  | "SIGNATORY"
  | "SECURITIES"
  | "INTERACTIVE_BROKERS"
  | null;

export type CompanyType =
  | "ALLARIA"
  | "ALLARIAMAS"
  | "AGROFINANZAS"
  | "ALLARIA_SECURITY"
  | "ALLARIA_ASSET_MANAGEMENT";

export type PersonType = "PHYSICAL" | "JURIDICAL";

export type DocumentRepresentativeType = "DNI_FRONT" | "DNI_BACK" | "SELFIE";

export type DocumentIndividualType =
  | DocumentRepresentativeType
  | "WORTH_STATEMENT";

export type DocumentType =
  | "DNI_FRONT"
  | "DNI_BACK"
  | "SELFIE"
  | "WORTH_STATEMENT"
  | "PROOF_OF_ADDRESS"
  | "PASSPORT_FIRST_PAGE"
  | "PASSPORT_SECOND_PAGE";

export type DocumentBusinessType =
  | "BALANCE" // 3 Balance certificado por CPCE o doc. patrimonial
  | "WORTH_STATEMENT" // 7 Documentación patrimonial
  | "SHARES_REGISTER" // 4 Registro de accionistas
  | "OTHERS" // 13 Otros
  | "STATUTE" // 1 Estatuto o contrato social escaneado
  | "POA" // 2 Poder de los firmantes
  | "AUTHORITIES" // 5 Acta de designación de autoridades
  | "MEMBERS_NOTE" // 10 Nota de miembros
  | "SHAREHOLDERS_NOTE" // 12 Nota de accionistas y beneficiarios finales
  | "TRUST_NOTE" // 11 Nota de fidunciantes, fiduciarios y beneficiarios
  | "PARTNERS_NOTE" // 8 Nota de socios protectores y beneficiarios
  | "PARTNERS_LIST" // 9 Nómina de socios
  | "EXPENSE_LIQUIDATION"
  | "UIF_INSCRIPTION_CERTIFICATE" // 6 "Constancia de inscripción ante UIF
  | "HORIZONTAL_PROPERTY_REGULATIONS"
  | "TRUST_CONSTITUTION_CONTRACT"
  | "CO_OWNER_LIST";

export interface Steps {
  step: number | null;
  subStep: number | null;
}

export interface PreconfirmationField {
  name: string;
  label: string;
  type?: InputType;
  options?: Option[];
  isConditional?: boolean;
  formatInPreConfirmation?: (
    value: string,
    formValues?: FormikValues | null
  ) => string;
  values?: FormikValues | null;
  valueGetter?: (values: FormikValues) => string;
  canUploadMultipleFiles?: boolean;
}

export interface PreconfirmationStep {
  name: string;
  columns: number;
  fields?: PreconfirmationField[];
  isDocumentStep?: boolean;
  fieldsWithSeparator?: PreconfirmationField[][];
  editStep?: {
    step: number;
    substep?: number;
  };
  hideStep?: boolean;
}

export interface PreconfirmationCheckbox {
  name: string;
  label: ReactNode;
  initialValue: boolean;
  required: boolean;
  validation: Schema;
  links?: {
    id: string;
    name: string;
    url: string;
  }[];
}

export interface Preconfirmation {
  headerContent?: ReactNode;
  steps: PreconfirmationStep[];
  checkboxes?: PreconfirmationCheckbox[];
  sendTermsAndConditions?: (
    state: RootState,
    values: FormikValues
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  tabs?: {
    label: string;
    steps: PreconfirmationStep[];
  }[];
  defaultSelectedKey?: (props: {
    dispatch: AppDispatch;
    state: RootState;
  }) => TabsProps["defaultSelectedKey"];
  submitButtonText?: (props: {
    state: RootState;
    formValues: FormikValues | null;
  }) => string;
  disabledKeys?: (props: {
    dispatch: AppDispatch;
    state: RootState;
  }) => TabsProps["disabledKeys"];
  onChangeTab?: (props: {
    key: Key;
    dispatch: AppDispatch;
    state: RootState;
  }) => void;
  onSubmit?: (props: {
    values: FormikValues;
    state: RootState;
    dispatch: AppDispatch;
    navigate: NavigateFunction;
  }) => void;
}

export interface Template {
  formTitle: string;
  logo: string;
  steps: Array<Step> | null;
  remoteSteps?: {
    link: string;
    step: Step;
  }[];
  icon?: string;
  onboardingType: OnboardingType;
  preconfirmation:
    | null
    | (({ values }: { values?: FormikValues | null }) => Preconfirmation);
  favicon: string;
  onTemplateCompleted?: (props: {
    values: FormikValues;
    state: RootState;
    dispatch: AppDispatch;
    navigate: NavigateFunction;
  }) => void;
}

export interface Step extends BasicStep, ComponentStep {
  subSteps?: Array<BasicStep | ComponentStep>;
}

interface StepBase {
  step: number | string;
  name?: string;
  label?: string;
  lastSubstepToGoBack?: (formik: FormikValues) => number;
  disableBackNavigation?: boolean | ((values: FormikValues, nextStepStatus: "completed" | "disabled") => boolean);
  hideStepper?: boolean;
  hideLayoutButtons?: boolean;
  disableButtonBack?: boolean;
  icon?: string;
  hideFromStepper?: boolean;
  showLanguagePicker?: boolean;
  // This could probably be merged with `hideStepper`
  hideFromSubStepper?: boolean;
  supplementaryComponentInfo?: SupplementaryComponentInfoProps;
}

export interface BasicStep extends StepBase {
  fieldsSections?: FieldsSection[];
  fields?: Field[];
  subtitle?: string;
  titleOfStep?: string;
  onContinue?: OnContinue;
  onBack?: OnBack;
}

export type ComponentProps = {
  dispatch?: AppDispatch;
  state?: RootState;
  formik?: FormikProps<FormikValues>;
};

export type Component = (
  dispatch: AppDispatch,
  state?: RootState,
  formik?: FormikProps<FormikValues>
) => JSX.Element;

type ButtonHandler = (
  values: FormikValues,
  dispatch?: AppDispatch,
  state?: RootState,
  setFieldValue?: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Promise<any>;

export type OnContinue = ButtonHandler;
export type OnBack = ButtonHandler;

export interface SecondaryForm {
  title?: (activeIndex?: number) => string;
  collectionName: string;
  fields: Field[];
  onSave: (
    values: FormikValues,
    dispatch?: AppDispatch,
    state?: RootState,
    setPrimaryFormValues?: (
      values: React.SetStateAction<FormikValues>,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<FormikValues>>,
    formik?: FormikProps<FormikValues>
  ) => void;
  primaryForm?: FormikProps<FormikValues>;
  tabs?: {
    options: {
      id: string;
      label: string;
    }[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    active: any;
    onChange: (
      tab: string,
      setActiveTab: React.Dispatch<React.SetStateAction<string>>,
      dispatch?: AppDispatch,
      state?: RootState,
      values?: FormikValues
    ) => void;
    disabled: (state: RootState) => boolean;
  };
  alert?: {
    id: string;
    title: string;
    message: string;
    primaryButton: {
      text: string;
      action: (tab?: string, dispatch?: AppDispatch, state?: RootState) => void;
    };
  };
  onSuccessCondition?: (state?: RootState) => boolean;
  onSuccess?: () => JSX.Element;
  successMessage?: string;
}

export interface ComponentStep extends StepBase {
  component?: Component;
  insideForm?: boolean;
  secondaryForm?: SecondaryForm;
  containerClassName?: string;
}

export interface SupplementaryComponentInfoProps {
  component: () => ReactNode;
  show: boolean | ((state: RootState) => boolean);
  position: "top" | "bottom";
}

export type InputType =
  | "text"
  | "email"
  | "select"
  | "radio"
  | "phone"
  | "file"
  | "token"
  | "multiple-radio"
  | "textarea"
  | "address"
  | "number"
  | "dni"
  | "cuit"
  | "label"
  | "date-picker"
  | "checkbox"
  | "date"
  | "button"
  | "multiple-checkbox"
  | "money"
  | "alert";

export interface Field {
  initialValue: string | boolean;
  type: InputType;
  dynamicType?: (
    formik: FormikProps<FormikValues>,
    state?: RootState
  ) => InputType;
  name: string;
  label: string;
  conditionalValidation?: (state?: RootState) => Schema;
  validation?: Schema;
  options?: Option[] | SegmentedInputOption[];
  /**
   * If the field is conditional, it will only be shown if the conditional field has the specified value
   */
  conditional?: Conditional | Conditional[];
  allConditionalsTrue?: boolean;
  conditionalBool?: (
    values: FormikValues,
    dispatch?: AppDispatch,
    state?: RootState
  ) => boolean;
  /**
   * If the field is conditionalFile, it will only be shown if the conditional field has the specified
   * value and the file is uploaded
   * */
  conditionalFile?: ConditionalFile;
  conditionalTooltip?: ConditionalTooltip | ConditionalTooltip[];
  placeholder?: string;
  selectType?: SelectType;
  validTypes?: string[];
  canUploadMultipleFiles?: boolean;
  infoTextPosition?: "top" | "bottom";
  infoText?: string | ((value: string | undefined) => string | undefined);
  tooltip?: Tooltip;
  maxSize?: number;
  confirmSelection?: boolean;
  columns?: number;
  disabled?: boolean | ((state: RootState, values: FormikValues) => boolean);
  isNumberPercentage?: boolean;
  minDatePickerDate?: Date;
  maxDatePickerDate?: Date;
  preconfirmationLabel?: string;
  /* Object formik value path */
  autoSelect?: string;
  country?: string;
  formatInPreConfirmation?: (
    value: string,
    formValues?: FormikValues | null
  ) => string;
  onChange?: OnChange;
  defaultSelectedKeys?: Values;
  onBlur?: OnChange;
  showExternalBox?: boolean;
  showOptionalLabel?: boolean;
  groupReorder?: string;
  autoComplete?: "on" | "off";
  dontScrollTo?: boolean;
  anchor?: {
    text: string;
    onClick?: () => void;
  };
  maxLength?: number;
  valueGetter?: (values: FormikValues) => string;
  /**
   * If the field is of type "select" and the options need to change dynamically based on other form values,
   * this function will be called to generate the options.
   * The function receives the current Formik props and should return an array of options.
   */
  dynamicOptions?: (
    formik: FormikProps<FormikValues>,
    state?: RootState
  ) => Option[];
  disabledByUrlParams?: string | string[];
  text?: ReactNode;
  buttonProps?: {
    text: string;
    variant?: ButtonProps["variant"];
    type?: ButtonProps["type"];
    startContent?: ReactNode;
    disabled?: (values: FormikValues, state: RootState) => boolean;
    onClick?: (
      values: FormikValues,
      dispatch?: AppDispatch,
      state?: RootState
    ) => void;
  };
  /* Hide input's label (if any) */
  hideLabel?: boolean;
  /** Conditionally display the field based on a step condition */
  hideField?: boolean;
  className?: string; // For styling the input container
  /* Avoid truncating options' text on "select" type fields */
  multilineSelect?: boolean;
  /* For type 'alert' */
  alertContent?: () => ReactNode;
}

export interface FieldsSection {
  name: string;
  fields: Field[];
  conditional?: Conditional | Conditional[];
  allConditionalsTrue?: boolean;
}

export type OnChange = (
  values: FormikValues,
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
  setFieldError?: (field: string, message: string | undefined) => void,
  setFieldTouched?: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void,
  element?: React.ChangeEvent<HTMLInputElement>,
  state?: RootState,
  dispatch?: AppDispatch
) => void;

export type OnValidation = (state?: RootState) => Schema;

export interface Tooltip {
  label?: string | ReactNode;
  modal?: string;
  icon?: string;
  content?: string;
  width?: string | number;
  /**
   * For cases where the tooltip is needed to be rendered outside the input.
   * For example, when the input is disabled and the tooltip needs to be enabled.
   */
  external?: boolean;
}

export interface ButtonData {
  text: string;
  isLoading: boolean;
  disabled: boolean;
  onClick: () => void;
  type?: "submit" | "button";
  show?: boolean;
}

export interface Option {
  value: string;
  label: string;
}

export interface SegmentedInputOption {
  value: string;
  label: string;
  checked: boolean;
}

export interface Conditional {
  name: string;
  value: string | (string | undefined)[] | File | boolean;
}

export interface ConditionalFile {
  name: string;
}

export interface ConditionalTooltip extends Conditional {
  tooltip: Tooltip;
}

export interface StepperProps {
  steps: Steps;
  setSteps: React.Dispatch<React.SetStateAction<Steps>>;
  template: Template | null;
}

export type Email = string;
export type DraftUUID = string;
export type GenderInitials = "M" | "F";
export type Gender = "MALE" | "FEMALE" | "OTHER";
export type CivilStatus = "SINGLE" | "MARRIED" | "DIVORCED" | "WIDOWED";
export type IdentityType = "DNI";
export type IdentityVersion = "A" | "B" | "C" | "D" | "E";
export type Pep = "DIRECT" | "FAMILY";
export type EmploymentYears =
  | "Menos de un año"
  | "1 año"
  | "2 años"
  | "3 años"
  | "4 años"
  | "5 años"
  | "6 años"
  | "7 años"
  | "8 años"
  | "9 años"
  | "Más de 10 años";
export type EmploymentStatus =
  | "DEPENDENCY_RELATIONSHIP"
  | "STUDENT"
  | "INDEPENDENT"
  | "MONOTAX"
  | "AUTONOMOUS"
  | "RETIRED"
  | "UNEMPLOYED";

export type CorporateType =
  | "SA - Sociedad Anónima"
  | "SRL - Sociedad de Responsabilidad Limitada"
  | "Asociación / Cámara / Cooperativa / Fundación / Mutual"
  | "Fideicomiso"
  | "SAS - Sociedad por Acciones Simplificada"
  | "SGR - Sociedad de Garantía Recíproca"
  | "Organismo y/o Entidad Gubernamental"
  | "Consorcio"
  | "Otro";

export type CorporateTypeTranslated =
  | "saCorporation"
  | "llcLimitedLiabilityCompany"
  | "associationFoundationCooperativeChamber"
  | "trust"
  | "simplifiedJointStockCompany"
  | "reciprocalGuaranteeCompany"
  | "consortium"
  | "governmentAgencyOrEntity"
  | "other";

export type DniImageType = "frontDNI" | "backDNI";

export interface Entity {
  label: string;
  value: string;
  startContent?: ReactNode;
  placeholder?: string;
}

export interface FirebaseDniData {
  scannedQR: boolean;
  frontDNI: string;
  backDNI: string;
}

export interface AuthToken {
  email: string;
  signatureRequestId: number;
  token: string;
}
export interface AuthSignatoryToken extends AuthToken {
  externalCode: string;
}

export interface RegenToken {
  email: string;
  signatureRequestId: number;
  companyType: CompanyType;
  partnerCompanyType?: PartnerCompanyType;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  country?: string;
  number?: number;
  floor?: number;
  apartment?: string;
  zipCode?: string;
  municipality?: string;
  fullAddress?: string;
}

export interface IdentityDocument {
  type: IdentityType;
  idNumber: string;
  procedureId: string;
  version: IdentityVersion;
  creationDate: string;
  expirationDate: string;
}

export interface RepresentativeInformation {
  email?: Email;
  cuit: string;
  name: string;
  lastName: string;
  gender: Gender;
  civilStatus: CivilStatus;
  birthDate: string;
  citizenship: string;
  birthCountry: string;
  telephone: string;
  pep?: Pep;
  addresses: Address[];
  identityDocument: IdentityDocument;
  pepDetails?: string;
  activities?: string[];
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface Representative {
  "@type": string;
  referrer: string;
  representativeInformation: RepresentativeInformation;
  validations: Validation[];
}

export interface Validation {
  name: string;
  externalCode: string;
}

export interface InvestmentProfile {
  choices: string[];
}

export interface PersonalInformation {
  email?: string;
  cuit: string;
  name: string;
  lastName: string;
  gender: Gender;
  civilStatus: string;
  birthDate: string;
  citizenship: string;
  birthCountry: string;
  telephone: string;
  addresses: Address[];
  identityDocument: IdentityDocument;
  pep: string;
  pepDetails?: string;
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface Shareholder {
  taxId: string;
  taxType: "CUIT" | "NIF";
  ownership: number | string;
  foreignTaxDetailBool?: "NO" | "YES";
  foreignTaxDetail: ForeignTaxDetailWithAddress;
}

export interface PhysicalShareholder extends Shareholder {
  personType: "PHYSICAL";
  name: string;
  lastName: string;
  citizenship: string;
  birthDate: string;
  civilStatus: CivilStatus | null;
  address: Address;
  activities: string[];
  pep?: Pep;
  pepDetails?: string;
  spouseName?: string;
  spouseLastName?: string;
  spouseCuil?: string;
}

export interface PhysicalShareholderWithSpouseObject
  extends PhysicalShareholder {
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface PhysicalShareholderWithSpouseObject
  extends Omit<
    PhysicalShareholder,
    "spouseName" | "spouseLastName" | "spouseCuil"
  > {
  spouse?: {
    name: string;
    lastName: string;
    cuit: string;
  };
}

export interface PhysicalShareholderFormik extends PhysicalShareholder {
  address: AddressWithFullAddress;
}

interface AddressWithFullAddress extends Address {
  fullAddress: string;
  stateSelect?: string;
  stateText?: string;
}

export interface JuridicalShareholder extends Shareholder {
  personType: "JURIDICAL";
  legalName: string;
}

export type ShareholderData = PhysicalShareholder | JuridicalShareholder | null;

export interface ForeignTaxDetail {
  idNumber: string;
  country: string;
}

export interface ForeignTaxDetailWithAddress {
  idNumber: string;
  address: Address;
}

export interface BankAccount {
  cxu: string;
}

export interface EmploymentProfile {
  activities: string[];
  years: EmploymentYears;
  employmentStatus: EmploymentStatus;
  bankAccounts?: BankAccount[];
  foreignTaxDetails?: ForeignTaxDetail;
}

export interface BusinessInformation {
  cuit: string;
  legalName: string;
  corporateType: CorporateType;
  activities: string[];
  activitiesDetails: string;
  registrationNumber: string;
  registrationAuthority: string;
  registrationAuthorityDetails: string;
  registrationCountry: string;
  registrationProvince: string;
  registrationDate: string;
  isPublicEntity: boolean;
  hasJointSignature: boolean;
  isInEconomicGroup: boolean;
  isComplexStructure: boolean;
  addresses: Address[];
  bankAccounts?: BankAccount[];
  foreignTaxDetail?: ForeignTaxDetailWithAddress;
}

export interface SignatoryData {
  email: string;
  name: string;
  lastName: string;
  idNumber?: string;
  confirmEmail?: string;
}

export type Signatory = SignatoryData | null;

export interface S {}

export interface StepData {
  step: number | string | null;
  subStep: number | null;
  disabledSubStep: number | null;
  completedSteps: number[] | null;
  reachedSteps: number[] | null;
  lastCompletedSubStep: number;
  numericStepList: number[] | null;
}

export type StepEndpoint =
  | StepOnePF
  | StepTwoPF
  | StepThreePF
  | StepFourPF
  | StepOnePJ
  | StepTwoPJ
  | StepThreePJ
  | StepFourPJ
  | StepFivePJ
  | StepSixPJ
  | StepSevenPJ
  | StepOneSecurities
  | StepTwoSecurities
  | StepThreeSecurities
  | StepFourSecurities
  | StepFiveSecurities
  | StepSixSecurities
  | StepSevenSecurities;
export type StepEndpointSecurities =
  | StepOneSecurities
  | StepTwoSecurities
  | StepThreeSecurities
  | StepFourSecurities
  | StepFiveSecurities
  | StepSixSecurities
  | StepSevenSecurities;

//----This step's types is only for BACK
//Steps of Physical Person
export interface StepOnePF {
  "@type": string;
  personalInformation: PersonalInformation;
  validations: Validation[];
  referrer: string;
  accountIdToLink: string | null;
}
export interface StepTwoPF {
  "@type": string;
  employmentProfile: EmploymentProfile;
}
export interface StepThreePF {
  "@type": string;
  investmentProfile: InvestmentProfile;
}

export interface StepFourPF {
  "@type": string;
  acceptedTermsAndConditions: boolean;
  acceptedOptionalTermsAndConditions: boolean;
  wantsAllariaAccount?: boolean;
}

// PITBULL

export type PitbullActivityPhysical = { name: string };

export type PitbullActivityJuridical = {
  activity: { name: string };
  externalCode: string;
};
export type PitbullActivities =
  | PitbullActivityPhysical[]
  | PitbullActivityJuridical[];

export type PitbullCountry = {
  citizenship: string;
  name: string;
  isoAlpha2: string;
  isoAlpha3: string;
};

export type PitbullCountries = PitbullCountry[];

export interface MidgardChoice {
  externalCode: string;
  option: string;
}

export interface MidgardQuestion {
  externalCode: string;
  text: string;
  companies: string[];
  choices: MidgardChoice[];
}
export type MidgardQuestions = MidgardQuestion[];

export interface PitbullProvince {
  name: string;
  isoAlpha2: string;
  countryIsoAlpha2: string;
}
export type PitbullProvinces = PitbullProvince[];

// Steps of Juridical Person
// TODO: Define the steps for Juridical Person
export type StepOnePJ = Representative;

export interface StepTwoPJ {
  "@type": string;
  businessInformation: BusinessInformation;
}

export interface StepThreePJ {
  "@type": string;
  additionalSignatories: Signatory[];
}

export interface StepFourPJ {
  "@type": string;
  shareholders: Array<PhysicalShareholder | JuridicalShareholder>;
}

export interface StepFivePJ {
  "@type": string;
  investmentProfile: InvestmentProfile;
}
export interface StepSevenPJ {
  "@type": string;
  representatives: StepOnePJ[];
}

export interface StepSixPJ {
  "@type": string;
  acceptedTermsAndConditions: boolean;
  acceptedOptionalTermsAndConditions: boolean;
}

export interface StepSevenPJ {
  "@type": string;
  representatives: Representative[];
}

export interface DocumentMetadata {
  type: string;
  addedBy: string;
  addedAt: string;
  originalFileName: string;
  contentType: string;
  publicUrl: string;
  uuid: string;
}

export type ErrorDetail = { field: string; message: string };

export interface ValidationErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: ErrorDetail;
  path: string;
}

export interface ErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  code: string;
  path: string;
}

export interface ConditionalBool {
  boolField: string | undefined;
  conditionalField: string;
  dontScrollTo: boolean;
  inputType: InputType;
}

type WithoutType<T> = Omit<T, "@type">;

export type StepEndpointResponse =
  | WithoutType<StepOnePF>
  | WithoutType<StepTwoPF>
  | WithoutType<StepThreePF>
  | WithoutType<StepFourPF>
  | WithoutType<StepOnePJ>
  | WithoutType<StepTwoPJ>
  | WithoutType<StepThreePJ>
  | WithoutType<StepFourPJ>
  | WithoutType<StepFivePJ>
  | WithoutType<StepSixPJ>
  | WithoutType<StepSevenPJ>;

export interface GetDraftResponse {
  companyType: CompanyType;
  email: string;
  externalCode: string;
  personType: PersonType;
  steps: StepEndpointResponse[];
}

export interface UploadedFile extends File {
  __alreadyUploaded?: boolean;
}

export interface SerializableFile {
  name: string;
  type: string;
  lastModified: number;
  size: number;
  blob: ArrayBuffer;
}

export interface SecuritiesOptions {
  gender: "MALE" | "FEMALE" | "OTHER";
  educationLevel:
    | "NONE"
    | "PRIMARY"
    | "SECONDARY"
    | "TERTIARY"
    | "UNIVERSITY"
    | "POSTGRADUATE"
    | "MASTER"
    | "DOCTORATE";
  civilStatus: CivilStatus | "CONCUBINAGE";
  documentType: "DNI" | "PASSPORT";
  taxIdType: "CUIT" | "CUIL" | "SSN" | "TAX_ID";
  investmentKnowlageLevel: "LIMITED" | "MODERATED" | "EXTENSIVE" | "NONE";
  investmentTool:
    | "ETF"
    | "ANNUITIES_VARIABLE"
    | "ANNUITIES_FIXED"
    | "UNITARY_INVESTMENT_TRUSTS"
    | "EXCHANGE_TRADED_FUNDS"
    | "FIXED_INCOME"
    | "INSURANCE"
    | "MUTUAL_FUNDS"
    | "COMMODITIES_FUTURES"
    | "EQUITIES"
    | "OPTIONS"
    | "PRECIOUS_METALS"
    | "REAL_ESTATE"
    | "OTHERS";
  investmentToolsKeys:
    | "annuitiesFixed"
    | "annuitiesVariable"
    | "etfs"
    | "uits"
    | "fixedIncome"
    | "insurance"
    | "mutualFunds"
    | "commoditiesAndFutures"
    | "equities"
    | "options"
    | "preciousMetals"
    | "realEstate"
    | "others";
  employmentStatus:
    | "EMPLOYEE"
    | "INDEPENDENT"
    | "RETIRED"
    | "UNEMPLOYED"
    | "STUDENT";
  accountObjective: "INCOME" | "LONG_TERM_GROWTH" | "SHORT_TERM_GROWTH";
  fundOrigin:
    | "ACCOUNTS_RECEIVABLE"
    | "RENTAL_INCOME"
    | "INCOME_FROM_PROFITS"
    | "MAINTENANCE"
    | "LEGAL_AGREEMENT"
    | "INSURANCE_INCOME"
    | "FAMILY"
    | "RETIREMENTS_SAVINGS"
    | "ROLL_OVER"
    | "BUSINESS_SALE"
    | "ACCUMULATED_SAVINGS"
    | "INVESTMENT_INCOME"
    | "GIFT"
    | "SALE_OF_REAL_ESTATE"
    | "LOTTERY_GAMES"
    | "OTHERS";
  timeHorizon: "ONE_OR_TWO_YEARS" | "TWO_TO_FIVE_YEARS" | "ABOVE_FIVE_YEARS";
  accountRiskProfile:
    | "CONSERVATIVE"
    | "MODERATE"
    | "AGGRESSIVE"
    | "SPECULATIVE";
  withdrawalReason:
    | "PERSONAL_EXPENSES"
    | "INVESTMENTS"
    | "SCHOOL_TUITION"
    | "OTHERS";
  withdrawalFrequency:
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUAL"
    | "ANNUAL"
    | "OTHER";
  transferReason:
    | "PERSONAL_EXPENSES"
    | "INVESTMENTS"
    | "SCHOOL_TUITION"
    | "OTHERS";
  transferFrequency:
    | "MONTHLY"
    | "QUARTERLY"
    | "SEMI_ANNUAL"
    | "ANNUAL"
    | "OTHER";
  investmentObjective:
    | "INCOME"
    | "LONG_TERM_GROWTH"
    | "SHORT_TERM_GROWTH"
    | "INCOME_AND_APPRECIATION"
    | "SPECULATION"
    | "CAPITAL_PRESERVATION"
    | "HEDGE"
    | "OTHER";
}

export interface StepOneSecurities {
  "@type": string;
  referredByCommercialTeam: boolean;
  referrer?: string;
  taxNumber: string;
  accountHolderSize: number;
  holdersAreMarried?: boolean;
  personalInformation: {
    isAmericanCitizen: boolean;
    completeName: string;
    motherLastName?: string;
    gender: SecuritiesOptions["gender"];
    countryOfBirth?: string;
    birthDate: string; // Format: MM/dd/yyyy
    civilStatus: SecuritiesOptions["civilStatus"];
    cellPhone: string;
    landline?: string;
    workPhone?: string;
    email: string;
    educationLevel: SecuritiesOptions["educationLevel"];
    numberOfDependants: number;
    isSameAddress: boolean;
    taxDomicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
    postalDomicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  };
}

export interface StepTwoSecurities {
  "@type": string;
  residence: {
    isAmericanResident: boolean;
    fiscalResidenceCountry?: string;
    primaryCitizenship: string;
    hasAdditionalCitizenship: boolean;
    additionalCitizenship?: string;
  };
  identityInformation: {
    identificationType: SecuritiesOptions["documentType"];
    number: string;
    documentIssuingCountry: string;
    documentIssuingDate: string; // Format: MM/dd/yyyy
    documentExpirationDate: string; // Format: MM/dd/yyyy
  };
}

interface StepThreeWorkInformation {
  workSituation: SecuritiesOptions["employmentStatus"];
  currentOccupation?: string;
  yearsInCurrentOccupation?: number;
  currentCompanyType?: string;
  previousOccupation?: string;
  yearsInPreviousOccupation?: number;
  employerInformation?: {
    name: string;
    address: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  };
}

// HACK: `workSituation` is on GET draft response, and `workInformation` is on POST step body
// both are the same, the name is misleading from the backend

export interface StepThreeSecurities {
  "@type": string;
  workInformation?: StepThreeWorkInformation;
  workSituation?: StepThreeWorkInformation;
  financialInformation: {
    annualIncomeFrom: number;
    annualIncomeTo: number;
    netIncomeFrom: number;
    netIncomeTo: number;
    liquidAssetsFrom: number;
    liquidAssetsTo: number;
  };
}

export interface StepFourSecurities {
  "@type": string;
  investmentKnowledge: {
    knowledgeLevel?: SecuritiesOptions["investmentKnowlageLevel"];
    instruments?: {
      [key in SecuritiesOptions["investmentTool"]]?: {
        knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"];
        since: number;
      };
    };
    haveAnotherInvestmentAccount: boolean;
    institutionName?: string;
    yearsOfExperienceInAccount?: number;
    familyAffiliation?: {
      brokerDealerName: string;
      employeeName: string;
      relationship: string;
    };
  };
}

export interface StepFiveSecurities {
  "@type": string;
  objectiveAndFunds: {
    objective: SecuritiesOptions["accountObjective"];
    originOfFunds: SecuritiesOptions["fundOrigin"];
    otherFundOrigin?: string;
    fundingPlan: number;
    initialTransferAmount: number;
    fundingSourceBankName: string;
    fundingSourceAccountHolder: string;
    timeHorizon: SecuritiesOptions["timeHorizon"];
  };
  riskProfile: SecuritiesOptions["accountRiskProfile"];
  transfersAndWithdrawals: {
    regularWithdrawals: boolean;
    withdrawalReason?: SecuritiesOptions["withdrawalReason"];
    otherWithdrawalReason?: string;
    thirdPartyTransfers: boolean;
    transferFrequency?: SecuritiesOptions["transferFrequency"];
    otherTransferFrequency?: string;
    thirdPartyTransfersReason?: SecuritiesOptions["transferReason"];
    otherThirdPartyTransfersReason?: string;
    withdrawalsFrequency?: SecuritiesOptions["withdrawalFrequency"];
    otherWithdrawalsFrequency?: string;
  };
  optionsAndMargins: {
    interestedInOptions: boolean;
    investmentObjectives?: SecuritiesOptions["investmentObjective"][];
    interestedInMargins: boolean;
  };
  interestedInAssigningBeneficiaries?: boolean;
  beneficiaries?: {
    name: string;
    assignedPercentage: number;
  }[];
}

export interface StepSixSecurities {
  "@type": string;
  beneficiariesInfo: {
    name: string;
    assignedPercentage: number;
    gender: SecuritiesOptions["gender"];
    birthDate: string; // Format: MM/dd/yyyy
    cellPhone: string;
    taxType: SecuritiesOptions["taxIdType"];
    taxNumber: string;
    relationshipWithBeneficiary: string;
    email: string;
    domicile: {
      address: string;
      postalCode: string;
      city: string;
      province: string;
      country: string;
      floor?: string | null;
      apartment?: string | null;
    };
  }[];
}

export interface HolderInformation {
  taxNumber: string /* String, required */;
  personalInformation: {
    /* PersonalInformationObsDTO, required */
    isAmericanCitizen: boolean /* Boolean, required */;
    completeName: string /* String, required */;
    motherLastName?: string /* String, optional */;
    gender: SecuritiesOptions["gender"] /* Gender, required (Values: MALE, FEMALE, OTHER) */;
    countryOfBirth?: string /* String, optional */;
    birthDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
    civilStatus: SecuritiesOptions["civilStatus"] /* CivilStatus, required (Values: SINGLE, MARRIED, DIVORCED, WIDOWED) */;
    cellPhone: string /* String, required (Valid Telephone format) */;
    landline?: string /* String, optional */;
    workPhone?: string /* String, optional */;
    email: string;
    educationLevel: SecuritiesOptions["educationLevel"] /* EducationLevel, required (Values: NONE, PRIMARY, SECONDARY, TERTIARY, UNIVERSITY, POSTGRADUATE, MASTER, DOCTORATE) */;
    numberOfDependants: number /* Integer, required */;
    isSameAddress: boolean /* Boolean, required */;
    taxDomicile: {
      /* required */ address: string /* String, required */;
      postalCode: string /* String, required */;
      city: string /* String, required */;
      province: string /* String, required */;
      country: string /* String, required */;
      floor?: string | null;
      apartment?: string | null;
    };
    postalDomicile: {
      /* required */ address: string /* String, required */;
      postalCode: string /* String, required */;
      city: string /* String, required */;
      province: string /* String, required */;
      country: string /* String, required */;
      floor?: string | null;
      apartment?: string | null;
    };
  };
  residence: {
    /* required */ isAmericanResident: boolean /* Boolean, required */;
    fiscalResidenceCountry?: string /* String, optional */;
    primaryCitizenship: string /* String, required */;
    hasAdditionalCitizenship: boolean /* Boolean, required */;
    additionalCitizenship?: string /* String, optional */;
  };
  identityInformation: {
    /* required */
    identificationType: SecuritiesOptions["documentType"] /* IdentificationType, required (Values: DNI, LE, LC, PASSPORT, CI) */;
    number: string /* String, required */;
    documentIssuingCountry: string /* String, required */;
    documentIssuingDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
    documentExpirationDate: string /* LocalDate, required (Format: MM/dd/yyyy) */;
  };
  workInformation: {
    /*required */
    workSituation: SecuritiesOptions["employmentStatus"] /* WorkSituation, required (Values: EMPLOYEE, INDEPENDENT, RETIRED, UNEMPLOYED, STUDENT) */;
    currentOccupation?: string /* String, optional */;
    yearsInCurrentOccupation?: number /* Integer, optional */;
    currentCompanyType?: string /* String, optional */;
    previousOccupation?: string /* String, optional */;
    yearsInPreviousOccupation?: number /* Integer, optional */;
    employerInformation?: {
      /* optional */ name: string /* String, required */;
      address: {
        /* AddressObsDTO, required */ address: string /* String, required */;
        postalCode: string /* String, required */;
        city: string /* String, required */;
        province: string /* String, required */;
        country: string /* String, required */;
        floor?: string | null;
        apartment?: string | null;
      };
    };
  };
  financialInformation: {
    annualIncomeFrom: number;
    annualIncomeTo: number;
    netIncomeFrom: number;
    netIncomeTo: number;
    liquidAssetsFrom: number;
    liquidAssetsTo: number;
  };
  investmentKnowledge: {
    /* required */
    knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"] /* KnowledgeLevel, required (Values: LIMITED, MODERATED, EXTENSIVE, NONE) */;
    instruments?: {
      [key in SecuritiesOptions["investmentTool"]]: {
        knowledgeLevel: SecuritiesOptions["investmentKnowlageLevel"];
        since: number;
      };
    };
    haveAnotherInvestmentAccount: boolean /* Boolean, required */;
    institutionName?: string /* String, optional */;
    yearsOfExperienceInAccount?: number /* Integer, optional */;
    familyAffiliation?: {
      brokerDealerName: string;
      employeeName: string;
      relationship: string;
    };
  };
}

export interface StepSevenSecurities {
  "@type": string;
  holders?: HolderInformation[] /* ArrayList<HolderInformationDTO>, optional */;
}

// Securities steps form types

export const BENEFICIARIES_LIMIT = 5;
// Create a utility type for beneficiary fields
export type BeneficiarySetupFields<N extends number> = {
  [K in `beneficiary${number extends N ? number : N}`]: {
    name: string;
  };
};
// Create a type that generates N beneficiary fields
export type GenerateBeneficiarySetupFields<N extends number> = {
  [K in keyof BeneficiarySetupFields<N>]?: BeneficiarySetupFields<N>[K];
} & {
  [K in `beneficiary${number extends N ? number : N}Percentage`]?: string;
};

// Create a utility type for detailed beneficiary fields
export type BeneficiaryFields<N extends number> = {
  [K in `beneficiary${number extends N ? number : N}`]: {
    name: string;
    gender: SecuritiesOptions["gender"];
    birthDate: string; // Format: DD/MM/YYYY
    email: string;
    phoneAR?: string;
    phoneUS?: string;
    taxIdType?: SecuritiesOptions["taxIdType"];
    taxIdNumber?: string;
    socialSecurityNumber?: string;
    relationship: string;
    country: string;
    fullAddress: string;
    city: string;
    zipCode: string;
    state: string;
    floor?: string;
    apartment?: string;
    documentFront: SerializableFile | null;
    documentBack?: SerializableFile | null;
  };
};

// Create a type that generates N detailed beneficiary fields
export type GenerateBeneficiaryFields<N extends number> = {
  [K in keyof BeneficiaryFields<N>]?: BeneficiaryFields<N>[K];
} & {
  [K in `beneficiary${number extends N ? number : N}Percentage`]?: string;
};

export interface SecuritiesFormikValues {
  prevStep: {
    email: string;
    taxId?: string;
    socialSecurityNumber?: string;
    referred?: string;
  };
  stepOne: {
    name: string;
    lastName: string;
    motherName?: string;
    gender: SecuritiesOptions["gender"];
    countryOfBirth: string;
    birthDate: string; // Format: DD/MM/YYYY
    civilStatus: SecuritiesOptions["civilStatus"];
    educationLevel: SecuritiesOptions["educationLevel"];
    dependants: string; // 0-11 string
    mobilePhoneAR?: string;
    mobilePhoneUS?: string;
    homePhoneAR?: string;
    homePhoneUS?: string;
    workPhoneAR?: string;
    workPhoneUS?: string;
    addressesSameAsFirstHolders: boolean;
    email: string;
    taxAddresses: {
      country: string;
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    postalAddressDifferent: boolean;
    postalAddresses?: {
      country: string;
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    proofOfAddress: SerializableFile | null;
  };
  stepTwo: {
    USResident: string; // SI or NO
    residenceCountry: string;
    firstCitizenship: string;
    additionalCitizenshipBool: string; // SI or NO
    additionalCitizenship?: string;
    socialSecurityNumber?: string;
    documentTypeAR?: SecuritiesOptions["documentType"];
    documentTypeUS?: SecuritiesOptions["documentType"];
    documentNumber?: string;
    passportNumber?: string;
    documentCountry: string;
    documentEmisionDate: string; // Format: DD/MM/YYYY
    documentExpirationDate: string; // Format: DD/MM/YYYY
    documentFrontDNI?: SerializableFile;
    documentBackDNI?: SerializableFile;
    documentFrontPASSPORT?: SerializableFile;
    documentBackPASSPORT?: SerializableFile;
    taxId?: string;
  };
  stepThree: {
    employmentStatus: SecuritiesOptions["employmentStatus"];
    occupation?: string;
    yearsWorked?: string; // number string
    companyType?: string;
    previousOccupation?: string;
    yearsPreviouslyWorked?: string; // number string
    companyName?: string;
    companyCountry?: string;
    companyAddresses?: {
      fullAddress: string;
      city: string;
      zipCode: string;
      state: string;
      floor?: string;
      apartment?: string;
    };
    annualIncomeLowerBound: string; // number string
    annualIncomeUpperBound: string; // number string
    netIncomeLowerBound: string; // number string
    netIncomeUpperBound: string; // number string
    netWorthLowerBound: string; // number string
    netWorthUpperBound: string; // number string
  };
  stepFour: {
    investmentsExperience: SecuritiesOptions["investmentKnowlageLevel"];
    investmentTools: {
      [key in SecuritiesOptions["investmentToolsKeys"]]: {
        bool: boolean;
        knowlage?: SecuritiesOptions["investmentKnowlageLevel"];
        years?: string; // number string
      };
    };
    hasOtherInvestmentAccountsBool: "SI" | "NO";
    otherInvestmentAccountName?: string;
    otherInvestmentAccountYears?: string; // number string
    isAffiliatedWithBroker: string; // SI or NO
    brokerName?: string;
    brokerEmployeeName?: string;
    brokerEmployeeRelationship?: string;
  };
  stepFiveFirstSubStep: {
    accountObjective: SecuritiesOptions["accountObjective"];
    originOfFunds: SecuritiesOptions["fundOrigin"];
    otherOriginOfFunds?: string;
    fundingDate: string; // number string, it represents the number of days
    initialTransferAmount: string; // number string
    fundingSourceBankName: string;
    fundingSourceAccountHolder: string;
    timeHorizon: SecuritiesOptions["timeHorizon"];
    riskProfile: SecuritiesOptions["accountRiskProfile"];
    regularWithdrawals: {
      bool: "SI" | "NO";
      reason?: SecuritiesOptions["withdrawalReason"];
      otherReason?: string;
      frequency?: SecuritiesOptions["withdrawalFrequency"];
      otherFrequency?: string;
    };
    thirdPartyTransfers: {
      bool: "SI" | "NO";
      reason?: SecuritiesOptions["transferReason"];
      otherReason?: string;
      frequency?: SecuritiesOptions["transferFrequency"];
      otherFrequency?: string;
    };
    tradeOptions: {
      bool: "SI" | "NO";
      options?: SecuritiesOptions["investmentObjective"][];
    };
    tradeMargins: {
      bool: "SI" | "NO";
    };
    beneficiariesBool: "SI" | "NO";
  } & GenerateBeneficiarySetupFields<1 | 2 | 3 | 4 | 5>;
  stepFiveSecondSubStep: GenerateBeneficiaryFields<1 | 2 | 3 | 4 | 5>;
}

export type PartnerCompanyType = "LANDTOKEN" | "LA_BRAGADENSE";
